import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMedia } from 'react-use';
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  IconButton,
} from '@c2fo/liquidity';
import { EllipsisVerticalIcon } from '@c2fo/liquidity/icons';
import { useBuyerNavigationLayoutContext } from '@/enterprise/components/BuyerNavigationLayout';
import useEnableAutomations from '@/enterprise/components/useEnableAutomations';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import RecurringRulesForm from '@/enterprise/features/recurringRules/RecurringRulesForm';
import { RecurringRulesMenuItem } from '@/enterprise/features/takerMarketTabs/components/TabActionsDropdown';
import { useReporting } from '@/reporting';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import useInvoiceDownloadUrl from '@/utils/useInvoiceDownloadUrl';
import useRestrictions from '@/utils/useRestrictions';

const EligibleInvoiceActionsDropdown = ({
  invoiceDownloadUrl,
  invoiceDownloadUrlDisabled,
  takerMarket,
}: {
  invoiceDownloadUrl?: string;
  invoiceDownloadUrlDisabled?: boolean;
  takerMarket: TakerMarket;
}) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const [showRecurringRulesModal, setShowRecurringRulesModal] = useState<boolean>(false);
  const { getRestrictions } = useRestrictions();
  const enableAutomations = useEnableAutomations();
  const { canEditRecurringRules } = getRestrictions([takerMarket]);
  const isMobile = useMedia('(max-width: 768px)');
  const {
    urlParams: { marketId, takerId },
  } = useBuyerNavigationLayoutContext();
  const invoiceDownloadUrlIncluded = useInvoiceDownloadUrl({
    filters: { included: [true] },
    marketId,
    takerId,
    type: 'eligible',
  });
  const invoiceDownloadUrlExcluded = useInvoiceDownloadUrl({
    filters: { included: [false] },
    marketId,
    takerId,
    type: 'eligible',
  });

  const openEligibleInvoiceActionsDropdown = () => {
    trackEnterpriseEvent('eligible-invoice-actions::clicked', {
      takerMarket: { takerId: takerMarket.takerDivisionId, marketUuid: takerMarket.marketUuid },
      type: 'division',
    });
  };

  const openRecurringRulesModal = () => {
    setShowRecurringRulesModal(true);
    trackEnterpriseEvent('recurring-rules::opened', {
      referrer: 'division-actions-dropdown',
    });
  };

  const closeRecurringRulesModal = () => {
    setShowRecurringRulesModal(false);
    trackEnterpriseEvent('recurring-rules::closed');
  };

  const downloadMenuItems = [
    {
      id: 'all',
      url: invoiceDownloadUrl,
      labelDesktop: t('core.allInvoices'),
      labelMobile: t('core.downloadAllInvoices'),
    },
    {
      id: 'included',
      url: invoiceDownloadUrlIncluded,
      labelDesktop: t('core.allIncludedInvoices'),
      labelMobile: t('core.downloadAllIncludedInvoices'),
    },
    {
      id: 'excluded',
      url: invoiceDownloadUrlExcluded,
      labelDesktop: t('core.allExcludedInvoices'),
      labelMobile: t('core.downloadAllExcludedInvoices'),
    },
  ];

  const renderDownloadMenuItems = () =>
    downloadMenuItems.map(({ id, url, labelDesktop, labelMobile }) => {
      const label = isMobile ? labelMobile : labelDesktop;

      return (
        <DropdownMenuItem key={id} asChild disabled={invoiceDownloadUrlDisabled}>
          <a href={url} target="_self">
            {label}
          </a>
        </DropdownMenuItem>
      );
    });

  const dropdownConfig = {
    dropdownMenuTrigger: (
      <IconButton icon={EllipsisVerticalIcon} name={t('taker.invoices.eligible.actionDropdown')} variant="primary" />
    ),
    dropdownMenuContent: (
      <>
        {enableAutomations ? (
          <RecurringRulesMenuItem onClick={openRecurringRulesModal} readOnly={!canEditRecurringRules} />
        ) : null}
        {isMobile ? (
          renderDownloadMenuItems()
        ) : (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              disabled={invoiceDownloadUrlDisabled}
              className={cn({ 'cursor-not-allowed opacity-38': invoiceDownloadUrlDisabled })}
            >
              {t('core.downloadInvoices')}
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent className="w-52">{renderDownloadMenuItems()}</DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        )}
      </>
    ),
  };

  return (
    <IntercomDataTarget target="eligibleInvoiceActionsDropdown">
      <RecurringRulesForm
        defaultValues={{ takerMarkets: [takerMarket] }}
        isForSingleDivision
        onClose={closeRecurringRulesModal}
        open={showRecurringRulesModal}
        referrer="divisionDetails"
        takerMarkets={[takerMarket]}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={openEligibleInvoiceActionsDropdown}>
          {dropdownConfig.dropdownMenuTrigger}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          avoidCollisions={false}
          collisionPadding={24}
          data-intercom-target="eligibleInvoiceActionsDropdownMenu"
        >
          {dropdownConfig.dropdownMenuContent}
        </DropdownMenuContent>
      </DropdownMenu>
    </IntercomDataTarget>
  );
};

export default EligibleInvoiceActionsDropdown;
